import { translate as t } from '@/lib/i18n';
import axios from 'axios';
import type { ApiErrorBody, ApiFieldError } from '@/types/api';

export class ApiError extends Error {
  private readonly messageEnglish?: string;
  readonly status: number;
  readonly path?: string;
  readonly fieldErrors?: ApiFieldError[];
  readonly rateLimitRemaining?: number;
  readonly rateLimitRetryAfter?: number;
  readonly timestamp?: string;
  readonly serverError?: string;

  constructor(params: {
    status: number;
    message: string;
    messageEnglish?: string;
    path?: string;
    fieldErrors?: ApiFieldError[];
    rateLimitRemaining?: number;
    rateLimitRetryAfter?: number;
    timestamp?: string;
    serverError?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.messageEnglish = params.messageEnglish;
    this.status = params.status;
    this.path = params.path;
    this.fieldErrors = params.fieldErrors;
    this.rateLimitRemaining = params.rateLimitRemaining;
    this.rateLimitRetryAfter = params.rateLimitRetryAfter;
    this.timestamp = params.timestamp;
    this.serverError = params.serverError;
  }

  get localizedMessage(): string {
    return this.messageEnglish ? t(this.message, this.messageEnglish) : this.message;
  }

  static from(err: unknown): ApiError {
    if (err instanceof ApiError) return err;

    if (axios.isAxiosError(err)) {
      const response = err.response;
      const headers = response?.headers ?? {};

      const rateLimitRemaining = parseHeaderInt(headers['x-ratelimit-remaining']);
      const rateLimitRetryAfter = parseHeaderInt(headers['x-ratelimit-retry-after-seconds']);

      if (response) {
        const body = response.data as Partial<ApiErrorBody> | undefined;
        const message = body?.message || response.statusText || err.message;
        return new ApiError({
          status: response.status,
          message: message || 'Erreur inconnue du serveur',
          messageEnglish: message ? undefined : 'Unknown server error',
          path: body?.path,
          // Backend shape : `errors` (liste de violations). On remappe sur
          // `fieldErrors` côté front pour ne pas collider avec `error`/`serverError`
          // dans les consumers React Hook Form qui itèrent `formState.errors`.
          fieldErrors: body?.errors,
          rateLimitRemaining,
          rateLimitRetryAfter,
          timestamp: body?.timestamp,
          serverError: body?.error,
        });
      }

      // Pas de réponse : réseau / timeout / CORS
      return new ApiError({
        status: 0,
        message:
          err.code === 'ECONNABORTED'
            ? 'Requête annulée (timeout)'
            : err.message && err.message !== 'Network Error'
              ? err.message
              : 'Erreur réseau',
        messageEnglish:
          err.code === 'ECONNABORTED'
            ? 'Request timed out'
            : !err.message || err.message === 'Network Error'
              ? 'Network error'
              : undefined,
      });
    }

    const message = err instanceof Error ? err.message : 'Erreur inconnue';
    return new ApiError({
      status: 0,
      message,
      messageEnglish: err instanceof Error ? undefined : 'Unknown error',
    });
  }

  isAuth(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }

  isConflict(): boolean {
    return this.status === 409;
  }

  isRateLimited(): boolean {
    return this.status === 429;
  }

  isValidation(): boolean {
    return this.status === 400 && !!this.fieldErrors?.length;
  }
}

function parseHeaderInt(value: unknown): number | undefined {
  // Axios normalise les headers réponse en string ; les autres types ne devraient
  // pas arriver ici, on les ignore défensivement.
  if (typeof value !== 'string') return undefined;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : undefined;
}
